/* eslint-disable camelcase */
import { getConfig, setConfig } from '~/contexts/stores/configStore';
import {
    isEmbedded,
    isInWebView,
    isTokenExpired,
    jwtDecode,
    reactNativeBridge,
} from '~/utils/helpers';
import {
    HANDOFF_SOURCE_WELFARE,
    WELFARE_AUTH_REQUIRED,
    WELFARE_HANDOFF_TOKEN,
    parseHostMessage,
} from '~/utils/handoffMessages';

/**
 * Acquiring a Welfare session from an embedding host.
 *
 * The Welfare app runs in three places: standalone in a browser tab, inside an
 * iframe in the Military App web app, and inside the mobile app's WebView. Only
 * the last two can hand it a session; standalone still logs in with a password,
 * and nothing here runs there.
 *
 * Framework-free on purpose. The boot path (useGetRefreshToken) and the axios
 * 401 interceptor both need to establish a session, and the interceptor is not
 * a React component, so this cannot be a hook.
 */

/** How long to wait for a host that may simply not be listening. */
const HANDOFF_TIMEOUT_MS = 15000;

/**
 * Origins permitted to hand this app a session, from
 * REACT_APP_HANDOFF_PARENT_ORIGINS (comma-separated).
 *
 * An empty list means no iframe host is trusted and the iframe path is off —
 * deliberately fail-closed, because the fallback of accepting any origin would
 * let any page on the internet frame this app and feed it tokens.
 *
 * @returns {String[]} the configured origins
 */
const allowedOrigins = (): string[] =>
    (process.env.REACT_APP_HANDOFF_PARENT_ORIGINS || '')
        .split(',')
        .map(origin => origin.trim())
        .filter(Boolean);

/**
 * Whether the app currently holds an access token that has not expired.
 *
 * This is the check behind "if the Welfare web app already has a valid access
 * token, ignore the handoff token and revoke it".
 *
 * @returns {Boolean} true when the stored token is present and still valid
 */
export const hasValidSession = (): boolean => {
    const { TOKEN } = getConfig();

    if (!TOKEN) return false;

    try {
        const { exp } = jwtDecode(TOKEN) || {};

        if (!exp) return false;

        return !isTokenExpired(exp * 1000);
    } catch {
        return false;
    }
};

/**
 * Send a message to whichever host is embedding us.
 *
 * The WebView bridge takes precedence: inside the mobile app both paths exist
 * (a WebView document also has a `window.parent`), and only the bridge reaches
 * React Native.
 *
 * @param {Object} message the message to post
 * @returns {void}
 */
const postToHost = (message: Record<string, unknown>) => {
    const bridge = reactNativeBridge();

    if (bridge) {
        bridge.postMessage(JSON.stringify(message));

        return;
    }

    const origins = allowedOrigins();

    // Never '*'. The request itself carries no secret, but a wildcard target
    // would announce to any framing page that this app is waiting for a token,
    // which is the opening move of the attack the origin allowlist prevents.
    origins.forEach(origin => {
        try {
            window.parent.postMessage(message, origin);
        } catch {
            // A parent that has gone away is not an error worth surfacing; the
            // timeout below is what ends the wait.
        }
    });
};

/**
 * Whether a message event may be believed.
 *
 * Inside the WebView there is no meaningful origin to check — the bridge
 * injects into this document directly and the event arrives with an empty or
 * synthetic origin — so the WebView case is trusted on the strength of the
 * bridge's presence. In an iframe the origin must be on the allowlist.
 *
 * @param {MessageEvent} event the incoming message
 * @returns {Boolean} true when the sender is trusted
 */
const isTrustedSender = (event: MessageEvent): boolean => {
    if (isInWebView()) return true;

    return allowedOrigins().includes(event.origin);
};

/**
 * Ask the host for a handoff token and wait for one.
 *
 * @returns {Promise<String|null>} the token, or null when none arrived
 */
const requestHandoffToken = (): Promise<string | null> =>
    new Promise(resolve => {
        let settled = false;

        const finish = (token: string | null) => {
            if (settled) return;

            settled = true;
            window.removeEventListener('message', onMessage);
            clearTimeout(timer);
            resolve(token);
        };

        const onMessage = (event: MessageEvent) => {
            if (!isTrustedSender(event)) return;

            const message = parseHostMessage(event.data);

            if (!message) return;

            // The host telling us it cannot help ends the wait immediately,
            // rather than leaving the user watching a spinner for the full
            // timeout for an answer that is never coming.
            finish(message.type === WELFARE_HANDOFF_TOKEN ? message.handoff_token : null);
        };

        window.addEventListener('message', onMessage);

        const timer = setTimeout(() => finish(null), HANDOFF_TIMEOUT_MS);

        postToHost({ source: HANDOFF_SOURCE_WELFARE, type: WELFARE_AUTH_REQUIRED });
    });

/**
 * Single-flight guard.
 *
 * Several requests can fail with 401 at once — the Cases screen alone fires a
 * handful in parallel — and each would otherwise ask the host for its own
 * handoff token. Every one of those is a separate mint, and all but the first
 * would be wasted: the exchange is single use, so the losers would spend tokens
 * to establish a session that already exists.
 */
let inFlight: Promise<boolean> | null = null;

/**
 * Turn a handoff token into a session, or give it back if it is not needed.
 *
 * This is the decision described in steps 5–8 of the flow, in one place, so
 * that a token which was pushed unasked and one that was requested are treated
 * identically.
 *
 * @param {String} handoffToken the token to spend or revoke
 * @param {Function} [setConfigFn] the ConfigContext setter, when the caller has one
 * @returns {Promise<Boolean>} whether a valid session is in place afterwards
 */
type SetConfigFn = (config: Record<string, any>) => void;

const consumeHandoffToken = async (
    handoffToken: string,
    setConfigFn?: SetConfigFn
): Promise<boolean> => {
    // Already authenticated: ignore the token and revoke it. Spending it would
    // pointlessly rotate a perfectly good session, and leaving it unspent would
    // leave a live credential in play for its full minute.
    if (hasValidSession()) {
        await revokeHandoffToken(handoffToken);

        return true;
    }

    // Imported lazily to break the cycle api.ts → handoffSession →
    // api/authentication → api.ts. A static import here leaves one of the three
    // modules half-initialised at load.
    const { onExchangeHandoffToken } = await import('~/api/authentication');

    const { token, refresh_token, api_key, email } = await onExchangeHandoffToken(handoffToken);

    if (!token) return false;

    const applyConfig = setConfigFn || setConfig;

    applyConfig({
        TOKEN: token,
        REFRESH_TOKEN: refresh_token,
        ...(api_key ? { API_KEY: api_key } : {}),
    });

    // A token on its own is not a working session: the per-tenant config block
    // and the member's id both come from /identity/status, and logging in has
    // always fetched them here. Without this the handoff produced a session
    // that looked fine until something read a server-provided config key.
    //
    // Not fatal if it fails -- the token is already stored and usable, and the
    // app retries this on its next boot -- so it must not undo a good exchange.
    if (email) {
        try {
            const { applyUserStatus } = await import('~/utils/sessionBootstrap');

            await applyUserStatus(email, { setConfig: applyConfig });
        } catch (error) {
            console.error('Session established but user status could not be applied', error);
        }
    }

    return true;
};

/**
 * Run a session acquisition under the single-flight guard.
 *
 * @param {Function} acquire produces the handoff token to consume
 * @param {Function} [setConfigFn] the ConfigContext setter, when the caller has one
 * @returns {Promise<Boolean>} whether the app holds a valid session afterwards
 */
const runExclusive = (
    acquire: () => Promise<string | null>,
    setConfigFn?: SetConfigFn
): Promise<boolean> => {
    if (inFlight) return inFlight;

    inFlight = (async () => {
        try {
            const handoffToken = await acquire();

            if (!handoffToken) return false;

            return await consumeHandoffToken(handoffToken, setConfigFn);
        } catch (error) {
            console.error('Handoff session could not be established', error);

            return false;
        } finally {
            inFlight = null;
        }
    })();

    return inFlight;
};

/**
 * Ask the embedding host for a session and wait for the answer.
 *
 * Resolves true when a usable access token is in place — including the case
 * where one was already there and nothing needed doing.
 *
 * @param {Function} [setConfigFn] the ConfigContext setter, when the caller has one
 * @returns {Promise<Boolean>} whether the app now holds a valid session
 */
export const establishSessionFromHost = async (
    setConfigFn?: SetConfigFn
): Promise<boolean> => {
    if (!isEmbedded()) return false;

    return runExclusive(requestHandoffToken, setConfigFn);
};

/**
 * Hand a token back unspent.
 *
 * Failure is swallowed: the token expires in under a minute regardless, and a
 * revoke that could not be delivered must not break a session that is otherwise
 * fine.
 *
 * @param {String} handoffToken the token to revoke
 * @returns {Promise<void>}
 */
export const revokeHandoffToken = async (handoffToken: string): Promise<void> => {
    try {
        const { onRevokeHandoffToken } = await import('~/api/authentication');

        await onRevokeHandoffToken(handoffToken);
    } catch (error) {
        console.error('Handoff token could not be revoked', error);
    }
};

/**
 * Listen for handoff tokens that arrive without having been asked for.
 *
 * This is the *primary* path for the web flow, not an edge case: the host mints
 * a token as it mounts the iframe and pushes it, rather than waiting to be
 * asked. A token that arrived unasked is consumed on exactly the same terms as
 * a requested one — spent when there is no session, revoked when there is.
 *
 * @param {Function} [onSessionChange] called with the result of each consumption
 * @param {Function} [setConfigFn] the ConfigContext setter, when the caller has one
 * @returns {Function} an unsubscribe function
 */
export const listenForUnsolicitedTokens = (
    onSessionChange?: (hasSession: boolean) => void,
    setConfigFn?: SetConfigFn
): (() => void) => {
    const onMessage = (event: MessageEvent) => {
        if (!isTrustedSender(event)) return;

        const message = parseHostMessage(event.data);

        if (!message || message.type !== WELFARE_HANDOFF_TOKEN) return;

        // A request already in flight owns whatever arrives: its own listener
        // resolves with this token and consumes it. Taking it here as well
        // would spend it twice, and the second exchange always loses.
        if (inFlight) return;

        const token = message.handoff_token;

        runExclusive(async () => token, setConfigFn).then(hasSession =>
            onSessionChange?.(hasSession)
        );
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
};
